import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
    authApi,
    LoginRequest,
    RegisterRequest,
    AuthResponse,
} from "@/api/auth";
import { queryClient } from "@/queryClient";
import { useAttendanceWatchStore } from "@/store/attendanceWatchStore";
import { useDeviceStore } from "@/store/deviceStore";
import { useGradingDraftStore } from './gradingDraftStore'

interface User {
    id: number;
    email: string;
    fullName: string;
    role: string; // 'owner' | 'admin' | 'admin_learner' | 'member' etc.
    canAccessAdmin: boolean;
    isOwner: boolean;          // LC RBAC
    lcPermissions: string[];   // LC RBAC
}

interface AuthState {
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    hasCheckedAuth: boolean; // 인증 확인이 완료되었는지 여부
    isHydrated: boolean; // Zustand persist rehydration 완료 여부
    postLogoutAllowedPath: string | null; // 로그아웃 직후 현재 화면을 1회성으로 유지할 경로
    error: string | null;
    errorCode: string | null; // 백엔드 ERROR_CODE (apiError i18n 매핑용 — 예: LEARNER_WITHDRAWN)

    // Actions
    login: (data: LoginRequest) => Promise<void>;
    register: (data: RegisterRequest) => Promise<void>;
    logout: (options?: { postLogoutAllowedPath?: string | null }) => void;
    checkAuth: () => Promise<void>;
    clearError: () => void;
    setHydrated: () => void; // rehydration 완료 표시
    applyUserUpdate: (partial: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            hasCheckedAuth: false, // 초기값은 false
            isHydrated: false, // rehydration 완료 전에는 false
            postLogoutAllowedPath: null,
            error: null,
            errorCode: null,

            setHydrated: () => {
                set({ isHydrated: true });
            },

            login: async (data) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await authApi.login(data);
                    const authData = response.data.data as AuthResponse;
                    const { user, accessToken, refreshToken } = authData;
                    const deviceContext = authData.deviceContext ?? null;
                    console.log("Logged in user:", user);

                    set({
                        user: {
                            id: user.id,
                            email: user.email,
                            fullName: user.fullName,
                            role: user.role || "member",
                            canAccessAdmin: user.canAccessAdmin ?? false,
                            isOwner: user.isLcOwner ?? false,
                            lcPermissions: user.lcPermissions ?? [],
                        },
                        accessToken,
                        refreshToken,
                        isAuthenticated: true,
                        isLoading: false,
                        hasCheckedAuth: true, // 로그인 시 인증 확인 완료
                        isHydrated: true, // 로그인 시 hydrated로 표시
                        postLogoutAllowedPath: null,
                    });

                    useDeviceStore.getState().setDeviceContext(deviceContext);
                    useAttendanceWatchStore
                        .getState()
                        .resetForLogin(
                            user.id,
                            deviceContext?.attendanceUpdated === true
                        );
                    sessionStorage.removeItem("unreg-banner-dismissed");
                } catch (error: any) {
                    set({
                        isLoading: false,
                        error:
                            error.response?.data?.message ||
                            "로그인에 실패했습니다.",
                        errorCode: error.response?.data?.error?.code ?? null,
                    });
                    throw error;
                }
            },

            register: async (data) => {
                set({ isLoading: true, error: null });

                try {
                    await authApi.register(data);
                    set({ isLoading: false });
                } catch (error: any) {
                    set({
                        isLoading: false,
                        error:
                            error.response?.data?.message ||
                            "회원가입에 실패했습니다.",
                    });
                    throw error;
                }
            },

            logout: (options) => {
                // React Query 캐시 초기화 (이전 사용자 데이터 제거)
                queryClient.clear();
                useDeviceStore.getState().clear();
                useAttendanceWatchStore.getState().clear();

                set({
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    postLogoutAllowedPath: options?.postLogoutAllowedPath ?? null,
                    // hasCheckedAuth는 유지 (로그아웃 후에도 이미 확인했다는 상태 유지)
                });
            },

            checkAuth: async () => {
                // App.tsx에서 isHydrated가 true일 때만 호출하므로
                // 여기서는 바로 인증 확인 진행
                const { accessToken } = get();

                if (!accessToken) {
                    useAttendanceWatchStore.getState().clear();
                    set({
                        isAuthenticated: false,
                        isLoading: false,
                        hasCheckedAuth: true, // 확인 완료
                        postLogoutAllowedPath: null,
                    });
                    return;
                }

                set({ isLoading: true });

                try {
                    // 타임아웃 설정 (10초)
                    const timeoutPromise = new Promise((_, reject) => {
                        setTimeout(
                            () => reject(new Error("Request timeout")),
                            10000
                        );
                    });

                    const response = (await Promise.race([
                        authApi.me(),
                        timeoutPromise,
                    ])) as any;

                    // ApiResponse<UserResponse> 형태이므로 response.data.data를 사용
                    const user = response.data.data;

                    useDeviceStore
                        .getState()
                        .setDeviceContext((user as any).deviceContext ?? null);
                    useAttendanceWatchStore.getState().ensureUser(user.id);

                    console.log("checkAuth user:", user);

                    set({
                        user: {
                            id: user.id,
                            email: user.email,
                            fullName: user.fullName,
                            role: user.role || "member",
                            canAccessAdmin: user.canAccessAdmin ?? false,
                            isOwner: user.isLcOwner ?? false,
                            lcPermissions: user.lcPermissions ?? [],
                        },
                        isAuthenticated: true,
                        isLoading: false,
                        hasCheckedAuth: true, // 확인 완료
                        postLogoutAllowedPath: null,
                    });
                } catch (error: any) {
                    console.error("checkAuth error:", error);
                    console.error("Error details:", error.response?.data);

                    // 토큰 만료 시 갱신 시도
                    if (error.response?.status === 401) {
                        const { refreshToken } = get();
                        if (refreshToken) {
                            try {
                                const refreshResponse = await authApi.refresh(
                                    refreshToken
                                );
                                const {
                                    user,
                                    accessToken: newAccessToken,
                                    refreshToken: newRefreshToken,
                                } = refreshResponse.data.data;

                                set({
                                    user: {
                                        id: user.id,
                                        email: user.email,
                                        fullName: user.fullName,
                                        role: user.role || "member",
                                        canAccessAdmin: user.canAccessAdmin ?? false,
                                        isOwner: user.isLcOwner ?? false,
                                        lcPermissions: user.lcPermissions ?? [],
                                    },
                                    accessToken: newAccessToken,
                                    refreshToken: newRefreshToken,
                                    isAuthenticated: true,
                                    isLoading: false,
                                    hasCheckedAuth: true,
                                    postLogoutAllowedPath: null,
                                });
                                useAttendanceWatchStore
                                    .getState()
                                    .ensureUser(user.id);
                                return;
                            } catch (refreshError) {
                                console.error(
                                    "Failed to refresh token:",
                                    refreshError
                                );
                                // 갱신 실패 시 로그아웃 진행
                            }
                        }
                    }

                    // 토큰 만료 등의 이유로 실패하면 로그아웃
                    // 상태 업데이트 및 localStorage 정리
                    set({
                        user: null,
                        accessToken: null,
                        refreshToken: null,
                        isAuthenticated: false,
                        isLoading: false,
                        hasCheckedAuth: true, // 확인 완료 - 에러가 발생해도 로딩 화면에서 벗어나게 함
                        postLogoutAllowedPath: null,
                    });
                    // localStorage에서도 인증 정보 제거 (일관성 유지)
                    try {
                        localStorage.removeItem("auth-storage");
                    } catch (e) {
                        // localStorage 접근 실패는 무시
                    }
                    useAttendanceWatchStore.getState().clear();
                }
            },

            applyUserUpdate: (partial) =>
                set((state) => ({
                    user: state.user ? { ...state.user, ...partial } : null,
                })),

            clearError: () => set({ error: null, errorCode: null }),
        }),
        {
            name: "auth-storage",
            partialize: (state) => ({
                // hasCheckedAuth와 isHydrated는 저장하지 않음 (항상 false로 시작)
                user: state.user,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                // isAuthenticated도 저장하지 않음 (항상 checkAuth()로 확인)
            }),
            onRehydrateStorage: () => {
                // rehydration 시작 시점
                return (state, error) => {
                    if (error) {
                        console.error("Failed to rehydrate auth state:", error);
                        // 에러 발생 시 모든 상태 초기화
                        if (state) {
                            state.user = null;
                            state.accessToken = null;
                            state.refreshToken = null;
                            state.isAuthenticated = false;
                            state.hasCheckedAuth = false;
                            state.isHydrated = true; // 에러여도 hydrated로 표시하여 무한 대기 방지
                            state.postLogoutAllowedPath = null;
                        }
                        return;
                    }

                    // localStorage에서 상태 복원 후, 인증 확인이 완료될 때까지
                    // isAuthenticated를 false로 초기화하여 checkAuth()가 완료될 때까지
                    // 인증 상태가 신뢰되지 않도록 함
                    if (state) {
                        // 복원된 토큰이 있어도 일단 인증 상태는 false로 시작
                        state.isAuthenticated = false;
                        state.hasCheckedAuth = false;
                        state.isHydrated = true; // rehydration 완료 표시
                        state.postLogoutAllowedPath = null;
                    }
                };
            },
        }
    )
);

// Unsaved grading belongs to the current account and center session.
useAuthStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id) useGradingDraftStore.getState().clear()
})
